import React, { useState, useEffect, useRef, useCallback } from 'react';
import { InputGroup, Input, Dropdown, DropdownMenu, DropdownItem, Badge, DropdownToggle, Button } from 'reactstrap';
import debounce from 'lodash.debounce';
import { useFetchData } from 'hooks/apiHooks';
import { API_ENDPOINTS } from 'constants';
import ApiError from '../common/ApiError';
import { useTranslation } from 'react-i18next';
import IconButton from 'components/iconInput/IconButton';
import AddButton from 'components/buttons/AddButton';
import IconButtonWithTooltip from 'components/buttons/IconButtonWithTooltip';
import { faSearch } from '@fortawesome/free-solid-svg-icons';

const ObjectSelectorDropdown = ({ onChange, obj, label, icon, setSearchMode }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    
    const type = typeof obj === 'string' ? obj : obj.type
    const filters = typeof obj === 'object' ? obj.filters : undefined
    
    const [filteredOptions, setFilteredOptions] = useState(null);
    const dropdownRef = useRef(null);
    const { data, isLoading, error, refetch} = useFetchData(API_ENDPOINTS[type.toUpperCase()].SEARCH, null, false);

    useEffect(() => {
        if(!filteredOptions && data){
            setFilteredOptions(data)
        }
    }, [data]);

    const debouncedSearch = useRef(
        debounce(async (term) => {
            const options = await refetch({searchTerm: term.toLowerCase(), filters: JSON.stringify(filters)})
            setFilteredOptions(options)
        }, 300)
    ).current;

    useEffect(() => {
        if(isOpen) {
            debouncedSearch(searchTerm);
        }
    }, [isOpen, searchTerm, debouncedSearch]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener(
                'mousedown',
                handleClickOutside
            );
        };
    }, []);

    const toggleDropdown = () => setIsOpen((prev) => !prev);

    const handleSearchChange = useCallback((e) => {
        const newSearchTerm = e.target.value;
        setSearchTerm(newSearchTerm);
        setIsOpen(true);
    }, []);

    const handleSelect = (option) => {
        onChange(option)
        setSearchTerm('');
        setIsOpen(false);
    };

    if (error) return <ApiError retryFetch={refetch} errorMessage={error.message}/>
    return (
        <Dropdown isOpen={isOpen} toggle={toggleDropdown} className="object-selector w-100">
            <DropdownToggle tag="div" className="w-100">
                <InputGroup>
                    {icon && <IconButton translationId={label} icon={icon}/>}
                    <Input
                        placeholder={t('objectSelector.searchAndSelect')}
                        value={searchTerm}
                        onChange={handleSearchChange}
                        aria-label={t('objectSelector.searchAndSelect')}
                        style={{marginTop:'4px', height:"38px", maxWidth:"13em"}}
                    />
                    {setSearchMode ? 
                        <AddButton onClick={() => {setSearchMode()}}/>
                    :
                        <IconButtonWithTooltip translationId='button.search' icon={faSearch} onClick={undefined} type="Button"/>
                    }
                </InputGroup>
            </DropdownToggle>
            <DropdownMenu style={{ width: '100%' }}>
                {filteredOptions?.length > 0 ? (
                    filteredOptions.map((option) => (
                        <DropdownItem
                            key={`${option.value}-${option.id}`}
                            onClick={() => handleSelect(option.id)}
                            role="option"
                        >
                            <Badge color="secondary" className="me-2">
                                {option.value}
                            </Badge>
                            {option.label}
                        </DropdownItem>
                    ))
                ) : (
                    <DropdownItem disabled>
                        {t('objectSelector.noResults')}
                    </DropdownItem>
                )}
            </DropdownMenu>
        </Dropdown>
    );
};

export default ObjectSelectorDropdown;
